import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
} from '@mui/material'
import { BUTTON_RADIUS } from '../../../theme/tokens'

/**
 * ConfirmDialog — Bestaetigung fuer Ausloeser, die KEIN Knopf sind.
 *
 * @description
 *   Die drei geteilten Bestaetigungsbausteine des Repos haengen alle an einem
 *   Knopf, den sie ersetzen: `InlineConfirmButton` klappt ihn auf, `HoldButton`
 *   laesst ihn halten, `ConfirmOnSelectDropdown` haengt an einem Menuewert.
 *   Ein Ausloeser in einem Overflow-Menue, einer Tabellenzeile oder einem
 *   Icon hat keinen solchen Knopf — dort stand bisher `window.confirm`, und das
 *   bringt den Browser-Dialog mit seinem eigenen Aussehen, ohne Design-Tokens,
 *   ohne Ueberschrift und ohne Busy-Zustand.
 *
 *   Anlass: die Gates zu PR 4 (R16-A W5) fanden vier `window.confirm`/
 *   `window.alert` in bereits beruehrten Dateien; das Konzept fuehrt sie unter
 *   F9 als „Rebuild".
 *
 * @useWhen
 *   - Ausloeser ohne eigenen Knopf (Overflow-Menue, Tabellenzeile, Icon)
 *   - Eine Rueckfrage mit EINER Konsequenz, die der Text nennen kann
 *   - Eine asynchrone Aktion, deren Laufzeit der Dialog zeigen soll (`busy`)
 *   - Rueckfrage aus einem OFFENEN Dialog, der Eingaben haelt — gestapelt
 *     (Pattern-Library §Modal from modal, „stack"; `AusfuhrKorrekturDialog`)
 *
 * @dontUseFor
 *   - Ein sichtbarer Knopf ausserhalb eines Dialogs → `InlineConfirmButton`
 *     (in einem Dialog hoert dessen Esc auch der aeussere Dialog)
 *   - Zerstörend ohne Rueckweg → `HoldButton` (Pattern C)
 *   - Mehrere Felder oder eine Auswahl → eigener Dialog
 *
 * @related
 *   - InlineConfirmButton — derselbe Zweck, aber am Knopf verankert
 *   - HoldButton — halten statt klicken, fuer das Unwiderrufliche
 *
 * @props
 *   open         boolean        Steuert die Sichtbarkeit.
 *   title        string         Ueberschrift; nennt den GEGENSTAND.
 *   message      node           Was passiert — inklusive der Folge.
 *   confirmLabel string         Beschriftung der Bestaetigung (Vorgabe "Confirm").
 *   cancelLabel  string         Beschriftung des Abbruchs (Vorgabe "Cancel").
 *   destructive  boolean        Faerbt die Bestaetigung `error` und stellt sie
 *                               an die zweite Stelle (Vorgabe false).
 *   busy         boolean        Sperrt beide Knoepfe. Die Bestaetigung traegt
 *                               waehrenddessen den Ladezustand des Hauses
 *                               (`loading` + `aria-busy`, M-17) — die
 *                               BESCHRIFTUNG bleibt `confirmLabel`.
 *   onConfirm    () => void     Wird beim Bestaetigen gerufen.
 *   onCancel     () => void     Wird bei Abbruch, Esc und Klick daneben gerufen.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy} sx={{ borderRadius: `${BUTTON_RADIUS}px` }}>
          {cancelLabel}
        </Button>
        {/* 🔴 M-17 (K-F1, 2026-10-10): der Ladezustand ist der des Hauses, nicht
            ein zweiter eigener. Bis hierher wechselte die BESCHRIFTUNG auf
            „Working…" und der Knopf blieb grau — die Form, die M-09/M-10
            abschaffen. `loading` bringt den Fortschrittsring, `aria-busy` die
            Meldung im Baum der Hilfstechnik (MUI setzt nur natives `disabled`,
            gemessen an `@mui/material` 7.3.5). `loadingPosition="start"` haelt
            die Beschriftung LESBAR: die Vorgabe `center` setzt `color:
            transparent` auf den Knopf und liesse den Text verschwinden. */}
        {/* 🔴 K-7 (UI2 Teil 8, 2026-10-10): hier stand bis heute DIESELBE
            `sx`-Radiuszeile wie am Abbrechen-Knopf — und sie war unter `sm`
            WIRKUNGSLOS. Die Theme-Regel `contained[down('sm')]` (T1-Hausform,
            `theme.js`) sitzt auf der Variantenklasse und gewinnt gegen die
            `sx`-Klasse: gemessen bei 390 px trug der Knopf die Pille, nicht die
            6 px der Zeile. Ueber `sm` traegt `root` ohnehin `BUTTON_RADIUS`.
            Eine zweite, tote Deklaration derselben Eigenschaft ist genau der
            Widerspruch, der den naechsten Leser in die Irre fuehrt — entfernt,
            nicht „repariert". Der Abbrechen-Knopf behaelt seine Zeile: bei ihm
            ist sie wirksam (fuer die Variante `text` gibt es keine solche
            Theme-Regel). */}
        <Button
          onClick={onConfirm}
          disabled={busy}
          loading={busy}
          loadingPosition="start"
          aria-busy={busy || undefined}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
